'use client'

// 903-б: подпись — из словаря каталога.
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { suggestCompanies, type CompanySuggestion } from '@/lib/actions/directory-suggest'

// ============================================================================
// ПОЛЕ НАЗВАНИЯ С ПОДСКАЗКОЙ ИЗ КАТАЛОГА.
// ----------------------------------------------------------------------------
// Один компонент на регистрацию и на «Добавить фирму» в админке: спрашивают они
// одно и то же, а разойдясь, начнут вести себя по-разному — и дубликаты снова
// пойдут с той стороны, где подсказку забыли обновить.
//
// ── ПОДСКАЗКА НЕ ЗАПРЕЩАЕТ ВВОД ────────────────────────────────────────────
// Иностранных компаний и вчера зарегистрированных юрлиц в каталоге нет. Поле
// остаётся обычным: не выбрал из списка — вписал руками, и ничто не мешает.
// ============================================================================

export function CompanyNameField({
  value, onChange, onPick, placeholder, className, id, required, suggest = suggestCompanies,
}: {
  value: string
  onChange: (name: string) => void
  /** Выбор из каталога: подставить код и город. */
  onPick: (c: CompanySuggestion) => void
  placeholder?: string
  className?: string
  id?: string
  required?: boolean
  /**
   * Откуда подсказка. По умолчанию — только каталог (регистрация). Админка
   * передаёт `suggestFirmNames`: там же наши карточки и кабинеты (jyDa28EJ).
   */
  suggest?: (query: string) => Promise<CompanySuggestion[]>
}) {
  const t = useTranslations('catalog.nameField')
  const [hits, setHits] = useState<CompanySuggestion[]>([])
  const [open, setOpen] = useState(false)
  const [picked, setPicked] = useState(false)

  useEffect(() => {
    // Выбрали из списка — не переспрашиваем: подсказка над готовым ответом
    // только мешает.
    if (picked) return
    if (value.trim().length < 3) { setHits([]); return }

    // Ищем не на каждую букву: человек печатает быстрее, чем отвечает база, и
    // запросы обгоняли бы друг друга.
    const t = setTimeout(() => {
      void suggest(value).then(r => { setHits(r); setOpen(true) }).catch(() => setHits([]))
    }, 350)

    return () => clearTimeout(t)
  }, [value, picked, suggest])

  return (
    <div className="relative">
      <input
        id={id}
        required={required}
        value={value}
        onChange={e => { setPicked(false); onChange(e.target.value) }}
        onFocus={() => { if (hits.length) setOpen(true) }}
        // Закрываем с задержкой: без неё нажатие на строку списка не успевает
        // сработать — поле теряет фокус раньше, чем приходит клик.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />

      {open && hits.length ? (
        <div className="absolute z-20 mt-1 max-h-[220px] w-full overflow-y-auto rounded-lg border border-ink-200 bg-white shadow-lg">
          {hits.map(c => (
            <button
              key={c.directoryId}
              type="button"
              onClick={() => {
                setPicked(true)
                setOpen(false)
                onChange(c.name)
                onPick(c)
              }}
              className="block w-full border-b border-ink-100 px-3 py-2 text-left last:border-0 hover:bg-ink-50"
            >
              <div className="flex items-center gap-1.5 text-[13px] font-semibold text-ink-900">
                <span className="min-w-0 truncate">{c.name}</span>
                {c.source ? (
                  <span className="shrink-0 rounded bg-accent-50 px-1.5 py-0.5 text-[10px] font-bold text-accent-700">
                    {t(c.source === 'cabinet' ? 'cabinet' : 'ourFirm')}
                  </span>
                ) : null}
              </div>
              <div className="text-[11.5px] text-ink-500">
                {c.code ? `IDNO ${c.code}` : t('noCode')}
                {c.city ? ` · ${c.city}` : ''}
              </div>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
